/**
 * Panel de correos entrantes (SES → S3 → SQS).
 * Permite al superadmin auditar lo que llega a `dte.clobi.io`, ver fallas de
 * routing y reprocesar manualmente (incluso forzando un tenant destino).
 */
import { DataTable } from '@/components/ds/DataTable'
import { useState } from 'react'
import { Loader2, Mail, RefreshCw, Search, AlertCircle, CheckCircle2, Clock, X } from 'lucide-react'
import { Button } from '@/components/Button'
import { formatDateTime, useTenantLocale } from '@/lib/locale'
import { Modal } from '@/components/ds/Modal'
import {
  useInboundEmails,
  useInboundEmailDetail,
  useReprocessInboundEmail,
  type InboundEmailStatus,
} from '../hooks/useInboundEmails'

const STATUS_CONFIG: Record<InboundEmailStatus, { cls: string; icon: typeof Mail }> = {
  received:  { cls: 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300', icon: Clock },
  routed:    { cls: 'bg-info-surface text-info-content', icon: Loader2 },
  processed: { cls: 'bg-success-surface text-success-content', icon: CheckCircle2 },
  failed:    { cls: 'bg-danger-surface text-danger-content', icon: AlertCircle },
  skipped:   { cls: 'bg-surface-muted text-content-muted', icon: X },
}

const SOURCE_LABELS: Record<string, string> = {
  to_header: 'To',
  delivered_to: 'Delivered-To',
  x_original_to: 'X-Original-To',
  received_for: 'Received: for (envelope SES)',
  return_path_verp: 'Return-Path VERP',
  manual: 'Asignación manual',
}

function StatusBadge({ status, label }: { status: InboundEmailStatus; label: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.received
  const Icon = cfg.icon
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <Icon size={11} className={status === 'routed' ? 'animate-spin' : ''} />
      {label}
    </span>
  )
}

export function InboundEmailsTab() {
  const locale = useTenantLocale()
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [days, setDays] = useState(30)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading } = useInboundEmails({
    status: statusFilter || undefined,
    search: search || undefined,
    days,
  })

  const counts = data?.counts_by_status ?? {}

  return (
    <div className="space-y-4">
      {/* Resumen por estado */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {(['received', 'routed', 'processed', 'failed', 'skipped'] as InboundEmailStatus[]).map(s => {
          const cfg = STATUS_CONFIG[s]
          const Icon = cfg.icon
          const count = counts[s] ?? 0
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
              className={`p-3 rounded-xl border text-left transition ${
                statusFilter === s
                  ? 'border-violet-400 bg-info-surface'
                  : 'border-divider bg-surface hover:bg-soft'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} className={cfg.cls.split(' ').filter(c => c.startsWith('text-')).join(' ')} />
                <span className="text-xs text-content-muted capitalize">{s}</span>
              </div>
              <div className="text-xl font-semibold text-content">{count}</div>
            </button>
          )
        })}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar por asunto, remitente o S3 key"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por asunto, remitente o S3 key…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-divider bg-surface text-content placeholder:text-content-subtle focus:outline-none focus:ring-2 focus:ring-focus-ring"
          />
        </div>
        <select aria-label="Período"
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="text-sm rounded-lg border border-divider bg-surface text-content px-3 py-2 focus:outline-none focus:ring-2 focus:ring-focus-ring"
        >
          <option value={1}>Últimas 24h</option>
          <option value={7}>Últimos 7 días</option>
          <option value={30}>Últimos 30 días</option>
          <option value={90}>Últimos 90 días</option>
        </select>
        {(statusFilter || search) && (
          <Button variant="secondary" size="sm" onClick={() => { setStatusFilter(''); setSearch('') }}>
            Limpiar
          </Button>
        )}
      </div>

      {/* Tabla */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-content-muted" />
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="text-center py-12 text-content-muted text-sm">
          <Mail size={32} className="mx-auto mb-2 text-content-subtle" />
          No hay correos para los filtros seleccionados.
        </div>
      ) : (
        <DataTable
          columns={[
            {
              key: 'recibido', header: 'Recibido',
              render: e => <span className="whitespace-nowrap text-content-muted">{formatDateTime(e.received_at, locale)}</span>,
            },
            {
              key: 'destinatario', header: 'Destinatario',
              render: e => (
                <div className="truncate max-w-[200px]" title={e.recipient_email}>
                  {e.recipient_email || <span className="text-content-subtle italic">no resuelto</span>}
                </div>
              ),
            },
            {
              key: 'remitente', header: 'Remitente',
              render: e => <div className="truncate max-w-[200px]" title={e.sender_email}>{e.sender_email}</div>,
            },
            {
              key: 'asunto', header: 'Asunto',
              render: e => <div className="truncate max-w-[280px]" title={e.subject}>{e.subject}</div>,
            },
            {
              key: 'tenant', header: 'Tenant',
              render: e => e.tenant_schema || <span className="text-content-subtle">—</span>,
            },
            {
              key: 'estado', header: 'Estado',
              render: e => <StatusBadge status={e.status} label={e.status_label} />,
            },
          ]}
          rows={data.items}
          onRowClick={e => setSelectedId(e.id)}
          caption="Correos recibidos"
        />
      )}

      {selectedId && (
        <InboundEmailModal id={selectedId} locale={locale} onClose={() => setSelectedId(null)} />
      )}
    </div>
  )
}

function InboundEmailModal({ id, locale, onClose }: { id: string; locale: string; onClose: () => void }) {
  const { data, isLoading } = useInboundEmailDetail(id)
  const reprocess = useReprocessInboundEmail()
  const [tenantOverride, setTenantOverride] = useState('')

  const handleReprocess = () => {
    reprocess.mutate(
      { id, tenant_schema: tenantOverride || undefined },
      {
        onSuccess: () => {
          setTenantOverride('')
          onClose()
        },
      },
    )
  }

  return (
    <Modal open onClose={onClose} title="Correo entrante" size="2xl" dismissOnScrimClick={false}>
      <div className="overflow-y-auto flex-1 min-h-0 space-y-4">
        {isLoading || !data ? (
          <div className="py-8 flex items-center justify-center"><Loader2 className="animate-spin" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Estado"><StatusBadge status={data.status} label={data.status_label} /></Field>
              <Field label="Recibido">{formatDateTime(data.received_at, locale)}</Field>
              <Field label="Procesado">{data.processed_at ? formatDateTime(data.processed_at, locale) : '—'}</Field>
              <Field label="Intentos">{data.attempts}</Field>
              <Field label="Remitente"><span className="break-all">{data.sender_email}</span></Field>
              <Field label="Destinatario resuelto">
                <span className="break-all">{data.recipient_email || <em className="text-content-subtle">no resuelto</em>}</span>
              </Field>
              <Field label="Origen del destinatario">
                {SOURCE_LABELS[data.recipient_source] ?? data.recipient_source ?? '—'}
              </Field>
              <Field label="Tenant">{data.tenant_schema || <em className="text-content-subtle">no resuelto</em>}</Field>
              <Field label="Asunto" wide><span className="break-all">{data.subject}</span></Field>
              <Field label="S3 key" wide><code className="text-xs break-all">{data.s3_bucket}/{data.s3_key}</code></Field>
            </div>

            {data.error_message && (
              <div className="p-3 rounded-lg bg-danger-surface border border-danger-border text-sm text-danger-content-strong">
                <div className="font-medium">{data.error_code || 'Error'}</div>
                <div className="mt-1">{data.error_message}</div>
              </div>
            )}

            {data.related_id && (
              <div className="p-3 rounded-lg bg-success-surface border border-success-border text-sm text-success-content-strong">
                Vinculado a {data.related_type === 'sii_notification' ? 'Notificación SII' : 'DTE recibido'}: <code className="text-xs">{data.related_id}</code>
              </div>
            )}

            <details className="text-sm">
              <summary className="cursor-pointer text-content-muted hover:text-content">Headers crudos</summary>
              <div className="mt-2 p-3 rounded-lg bg-glass-3">
                {Object.entries(data.headers || {}).map(([k, v]) => (
                  <div key={k} className="break-all"><span className="text-content-muted">{k}:</span> {v || <em>—</em>}</div>
                ))}
                {data.received_headers && data.received_headers.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-content-muted">Received ({data.received_headers.length})</summary>
                    <pre className="mt-1 text-xs whitespace-pre-wrap">{data.received_headers.join('\n\n')}</pre>
                  </details>
                )}
              </div>
            </details>
          </>
        )}
      </div>

      <div className="py-3 border-t border-divider shrink-0 flex items-center gap-2">
        <input aria-label="Tenant de destino"
          value={tenantOverride}
          onChange={e => setTenantOverride(e.target.value)}
          placeholder={data?.tenant_schema || 'Forzar tenant_schema (ej: zenlatam)'}
          className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-divider bg-surface text-content focus:outline-none focus:ring-2 focus:ring-focus-ring"
        />
        <Button onClick={handleReprocess} disabled={reprocess.isPending}>
          {reprocess.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Reprocesar
        </Button>
      </div>
    </Modal>
  )
}

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <div className="text-xs text-content-muted mb-0.5">{label}</div>
      <div className="text-content">{children}</div>
    </div>
  )
}
